import { memo, useState, type ReactNode } from 'react'
import lightningIcon from '@/assets/bitcoin-icons_lightning-outline.svg'
import ideaIcon from '@/assets/hugeicons_idea-01.svg'
import verifiedIcon from '@/assets/material-symbols_verified-outline.svg'
import travelExploreIcon from '@/assets/material-symbols-light_travel-explore.svg'
import searchInsightsIcon from '@/assets/material-symbols_search-insights.svg'
import economicGrowthIcon from '@/assets/streamline-flex_decent-work-and-economic-growth-solid.svg'
import { HomeScrollReveal } from '@/components/ui/home/HomeScrollReveal'
import { goldGradientButtonSm } from '@/lib/brandGradient'
import { cn } from '@/lib/cn'

type RoleTab = 'founders' | 'validators'

type StepCard = {
  stepLabel: string
  title: string
  description: string
  outcome: string
  icon: ReactNode
}

type RoleContent = {
  bannerLabel: string
  bannerTitle: string
  bannerDescription: string
  steps: StepCard[]
}

function AssetIcon({ src, alt = '' }: { src: string; alt?: string }) {
  return (
    <img
      src={src}
      alt={alt}
      aria-hidden={!alt}
      className="h-6 w-6 shrink-0 object-contain"
      draggable={false}
    />
  )
}

const IconChip = ({ children }: { children: ReactNode }) => (
  <div className="grid h-11 w-11 place-items-center rounded-[8px] border border-[#EFAE49] bg-transparent">
    {children}
  </div>
)

const howItWorksCardSurface =
  'border border-white/[0.08] bg-[#141414] dark:bg-[#141414]'

/**
 * Brighter, more saturated gold for the labels here. The shared light-gold
 * (#F6CB86) washes out because these cards remap to a white surface in light
 * mode — this deeper gradient reads clearly on both white and #141414.
 */
const howItWorksGoldText =
  'bg-gradient-to-r from-[#EFAE49] to-[#D2871E] bg-clip-text text-transparent'

/** Shared divider above “What you get” — same token on every step card. */
const howItWorksStepDivider = 'border-t border-white/[0.08]'

const CONTENT: Record<RoleTab, RoleContent> = {
  founders: {
    bannerLabel: 'FOUNDERS',
    bannerTitle: 'Test Your Startup Vision',
    bannerDescription: 'Gather honest feedback and build with confidence.',
    steps: [
      {
        stepLabel: 'Submit Your Idea',
        title: 'Five minutes, start to finish',
        description:
          "Share the problem you're solving, who it's for, and what makes your solution different. No pitch deck or business plan required.",
        outcome: 'Your idea goes live for community validation immediately.',
        icon: (
          <IconChip>
            <AssetIcon src={lightningIcon} />
          </IconChip>
        ),
      },
      {
        stepLabel: 'Community Validation',
        title: 'Real founders, investors and users',
        description:
          'For 7 days, validators assess your problem, solution, and market opportunity, providing structured feedback and insights.',
        outcome: 'Unfiltered feedback from people who matter.',
        icon: (
          <IconChip>
            <AssetIcon src={verifiedIcon} />
          </IconChip>
        ),
      },
      {
        stepLabel: 'Idea Feasibility Report',
        title: 'Know if your idea works',
        description:
          "After validation, you'll receive a focused report covering strengths, risks, opportunities and key improvement areas.",
        outcome:
          'Practical insights to make informed decisions about your idea.',
        icon: (
          <IconChip>
            <AssetIcon src={ideaIcon} />
          </IconChip>
        ),
      },
    ],
  },
  validators: {
    bannerLabel: 'VALIDATORS',
    bannerTitle: 'Your Feedback Shapes Innovation',
    bannerDescription: 'Evaluate ideas and influence what gets build next.',
    steps: [
      {
        stepLabel: 'Explore Ideas',
        title: 'Your Voice Shapes the Future',
        description:
          'Browse startup ideas from the Founders across different industries and stages.',
        outcome: 'Early access to innovative startup ideas.',
        icon: (
          <IconChip>
            <AssetIcon src={travelExploreIcon} />
          </IconChip>
        ),
      },
      {
        stepLabel: 'Share your insights',
        title: 'Turn Feedback Into Impact',
        description:
          'Review ideas, rate key aspects and provide constructive feedback.',
        outcome:
          'Opportunity to influence products before they reach the market.',
        icon: (
          <IconChip>
            <AssetIcon src={searchInsightsIcon} />
          </IconChip>
        ),
      },
      {
        stepLabel: 'Make an impact',
        title: 'Bring Great Ideas to Life',
        description:
          'Influence decisions, highlight blind spots and contribute to stronger ideas',
        outcome:
          'Get featured in the Leaderboard and build your reputation as an expert validator.',
        icon: (
          <IconChip>
            <AssetIcon src={economicGrowthIcon} />
          </IconChip>
        ),
      },
    ],
  },
}

function RoleToggle({
  active,
  onChange,
}: {
  active: RoleTab
  onChange: (tab: RoleTab) => void
}) {
  return (
    <div
      className="flex w-full max-w-full rounded-full border border-white/10 bg-[#141414] p-1 sm:inline-flex sm:w-auto sm:shrink-0"
      role="tablist"
      aria-label="How it works audience"
    >
      {(
        [
          { id: 'founders' as const, label: 'Founders' },
          { id: 'validators' as const, label: 'Validators' },
        ] as const
      ).map(({ id, label }) => {
        const selected = active === id
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(id)}
            className={cn(
              'min-h-[40px] flex-1 px-3 py-2 text-center text-sm font-semibold transition sm:min-h-0 sm:flex-none sm:px-5',
              selected
                ? goldGradientButtonSm
                : 'bg-transparent font-semibold text-white/80 hover:text-white',
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

function HeroBanner({ content }: { content: RoleContent }) {
  return (
    <div
      className={cn(
        'relative flex h-[168px] w-full max-w-[1200px] flex-col justify-center overflow-hidden rounded-[10px] border border-white/[0.08] px-6 sm:px-8',
        howItWorksCardSurface,
      )}
    >
      <div
        className="pointer-events-none absolute top-0 right-0 h-32 w-48 rounded-full bg-[#E0A263]/20 blur-3xl"
        aria-hidden
      />
      <div className="relative z-[1] text-left">
        <p
          className={cn(
            'text-xs font-medium tracking-[0.2em] uppercase',
            howItWorksGoldText,
          )}
        >
          {content.bannerLabel}
        </p>
        <h3 className="mt-2 text-2xl font-medium tracking-tight text-white sm:text-[26px]">
          {content.bannerTitle}
        </h3>
        <p className="mt-1.5 max-w-2xl text-sm leading-relaxed font-medium text-white/60 sm:text-[15px]">
          {content.bannerDescription}
        </p>
      </div>
    </div>
  )
}

function StepCardArticle({ step }: { step: StepCard }) {
  return (
    <article
      className={cn(
        'relative flex min-h-[340px] w-full max-w-[384px] flex-col rounded-[16px] px-6 pt-7 pb-6 text-left lg:w-[384px] lg:max-w-none',
        howItWorksCardSurface,
        '!shadow-none drop-shadow-none [filter:none]',
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {step.icon}
        <p
          className={cn(
            'mt-4 text-[18px] leading-[1.35] font-medium',
            howItWorksGoldText,
          )}
          style={{ fontSize: 18, fontWeight: 500 }}
        >
          {step.stepLabel}
        </p>
        <h4 className="mt-2 text-[20px] leading-snug font-medium text-white">
          {step.title}
        </h4>
        <p className="mt-2 flex-1 text-[14px] leading-relaxed font-medium text-white/65">
          {step.description}
        </p>
      </div>
      <div
        className={cn('mt-auto w-full shrink-0 pt-4', howItWorksStepDivider)}
      >
        <p className="text-[14px] leading-relaxed font-medium">
          <span className="text-white/55">What you get: </span>
          <span className="text-white/90">{step.outcome}</span>
        </p>
      </div>
    </article>
  )
}

export const HowItWorksSection = memo(() => {
  const [role, setRole] = useState<RoleTab>('founders')
  const content = CONTENT[role]

  return (
    <section
      className="relative w-full bg-[var(--background)] px-4 py-10 sm:px-6 sm:py-12 lg:py-14"
      aria-labelledby="how-it-works-heading"
    >
      <div className="relative mx-auto w-full max-w-[1200px]">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 text-left">
            <h2
              id="how-it-works-heading"
              className="text-[28px] leading-tight font-medium tracking-tight text-[var(--foreground)]"
            >
              How It Works
            </h2>
            <p className="mt-2 text-[14px] leading-relaxed font-medium text-[var(--foreground-muted)] max-sm:whitespace-normal sm:leading-snug sm:whitespace-nowrap">
              See how founders submit ideas, validators provide feedback, and
              insights help shape stronger opportunities.
            </p>
          </div>
          <div className="w-full sm:w-auto sm:shrink-0">
            <RoleToggle active={role} onChange={setRole} />
          </div>
        </header>

        <div className="mt-8 sm:mt-10">
          <HeroBanner content={content} />
        </div>

        <div
          className="mt-6 flex flex-col items-center gap-6 lg:mt-8 lg:flex-row lg:items-stretch lg:justify-between"
          role="tabpanel"
          aria-label={role === 'founders' ? 'For founders' : 'For validators'}
        >
          {content.steps.map((step, i) => (
            <HomeScrollReveal
              key={`${role}-${step.stepLabel}`}
              index={i}
              className="flex w-full justify-center lg:w-auto lg:shrink-0"
            >
              <StepCardArticle step={step} />
            </HomeScrollReveal>
          ))}
        </div>
      </div>
    </section>
  )
})

HowItWorksSection.displayName = 'HowItWorksSection'
